import React, { useCallback, useEffect, useMemo, useState } from "react";
import { usersApi, productsApi } from "../../services/api.service";
import { AGENTS_REFRESH_EVENT } from "../../hooks/useAgents";
import { useApp } from "../../store/AppContext";
import { useAuth } from "../../store/AuthContext";
import { T, Btn, SectionLabel, Field, Input, Select, Textarea, Card, Modal } from "../shared/UI";
import { fmt } from "../../utils/helpers";

const ROLES = ["SUPER_ADMIN", "ADMIN", "STAFF", "AGENT"];

/** Null for a missing timestamp, so callers can say "never" rather than show a dash. */
const fmtDate = (value) => (value ? fmt.date(value) : null);

export default function UsersView() {
  const { notify, usersModalOpen, usersModalMode, usersModalData, closeUsersModal } = useApp();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({ role: "", productId: "", search: "" });
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("create"); // create | edit
  const [formError, setFormError] = useState(null);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    mobile: "",
    password: "",
    city: "",
    district: "",
    area: "",
    designation: "",
    pincode: "",
    role: "AGENT",
    status: "on",
    alternateMobile: "",
    latitude: "",
    longitude: "",
    address: "",
    productIds: [],
  });

  const productNameById = useMemo(() => {
    const map = {};
    products.forEach(p => { map[p.id] = p.name; });
    return map;
  }, [products]);

  const selected = useMemo(
    () => users.find(u => String(u.id) === String(selectedId)) || null,
    [users, selectedId]
  );
  const canManageUsers = user?.role === "ADMIN" || user?.role === "SUPER_ADMIN";
  const canEditAdminPassword = user?.role === "SUPER_ADMIN";
  const canEditPassword = canManageUsers && (
    selected?.role === "AGENT" ||
    (selected?.role === "ADMIN" && canEditAdminPassword)
  );
  const canClearDevice = canManageUsers && selected?.role === "AGENT";

  const loadProducts = useCallback(async () => {
    try {
      const res = await productsApi.list();
      const raw = Array.isArray(res.data?.data) ? res.data.data
        : Array.isArray(res.data) ? res.data : [];
      setProducts(raw);
    } catch {
      // Product list is optional for non-agent roles
    }
  }, []);

  const loadUsers = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit: meta.limit,
        ...(filters.role ? { role: filters.role } : {}),
        ...(filters.productId ? { productId: filters.productId } : {}),
        ...(filters.search ? { search: filters.search } : {}),
      };
      const res = await usersApi.list(params);
      const raw = Array.isArray(res.data?.data) ? res.data.data
        : Array.isArray(res.data) ? res.data : [];
      const metaRes = res.data?.meta || { page, limit: meta.limit, total: raw.length, totalPages: 1 };
      setUsers(raw);
      setMeta({
        page: metaRes.page || page,
        limit: metaRes.limit || meta.limit,
        total: metaRes.total || raw.length,
        totalPages: metaRes.totalPages || 1,
      });
      if (raw.length && (!selectedId || !raw.find(u => String(u.id) === String(selectedId)))) {
        setSelectedId(raw[0].id);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to load users";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [filters, meta.limit]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadUsers(1);
  }, [loadUsers]);

  useEffect(() => {
    if (!usersModalOpen) return;
    if (usersModalMode === "create") {
      openCreate();
    } else if (usersModalMode === "edit" && usersModalData) {
      setSelectedId(usersModalData.id);
      openEdit(usersModalData);
    }
  }, [usersModalOpen, usersModalMode, usersModalData]);

  const resetForm = (overrides = {}) => {
    setForm({
      fullName: "",
      email: "",
      mobile: "",
      password: "",
      city: "",
      district: "",
      area: "",
      designation: "",
      pincode: "",
      role: "AGENT",
      status: "on",
      alternateMobile: "",
      latitude: "",
      longitude: "",
      address: "",
      productIds: [],
      ...overrides,
    });
  };

  const openCreate = () => {
    setMode("create");
    resetForm();
    setFormError(null);
    setModalOpen(true);
  };

  const applyTargetToForm = (target) => {
    if (!target) return;
    resetForm({
      fullName: target.fullName || "",
      email: target.email || "",
      mobile: target.mobile || "",
      city: target.city || "",
      district: target.district || "",
      area: target.area || "",
      designation: target.designation || "",
      pincode: target.pincode || "",
      role: target.role || "AGENT",
      status: target.status || "on",
      alternateMobile: target.alternateMobile || "",
      latitude: target.latitude || "",
      longitude: target.longitude || "",
      address: target.address || "",
      productIds: target.productId ? [target.productId] : [],
      password: "",
    });
  };

  const openEdit = async (target = selected) => {
    if (!target) return;
    setMode("edit");
    applyTargetToForm(target);
    setFormError(null);
    setModalOpen(true);
    if (!target.id) return;
    try {
      const res = await usersApi.detail(target.id);
      const detail = res.data?.data || res.data;
      if (detail) applyTargetToForm(detail);
    } catch {
      // fallback to target-only data
    }
  };

  useEffect(() => {
    if (!modalOpen || mode !== "edit" || !selected) return;
    applyTargetToForm(selected);
  }, [modalOpen, mode, selected]);

  const updateProductSelection = (id) => {
    const numId = Number(id);
    if (form.role === "AGENT") {
      setForm(f => ({ ...f, productIds: [numId] }));
      return;
    }
    setForm(f => {
      const next = new Set(f.productIds);
      if (next.has(numId)) next.delete(numId);
      else next.add(numId);
      return { ...f, productIds: Array.from(next) };
    });
  };

  const validateForm = () => {
    if (!form.fullName.trim()) return "Full name is required";
    if (!form.mobile.trim()) return "Mobile is required";
    if (mode === "create" && !form.password.trim()) return "Password is required";
    if (form.role === "AGENT" && form.productIds.length !== 1) {
      return "Agent role requires exactly one product";
    }
    return null;
  };

  const saveUser = async () => {
    const errMsg = validateForm();
    if (errMsg) {
      setFormError(errMsg);
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        city: form.city.trim(),
        district: form.district.trim(),
        area: form.area.trim(),
        designation: form.designation.trim(),
        pincode: form.pincode.trim(),
        role: form.role,
        status: form.status,
        alternateMobile: form.alternateMobile.trim(),
        latitude: form.latitude.trim(),
        longitude: form.longitude.trim(),
        address: form.address.trim(),
        ...(form.role === "AGENT" || form.productIds.length ? { productIds: form.productIds } : {}),
        ...(mode === "create" ? { password: form.password } : {}),
        ...(mode === "edit" && canEditPassword && form.password.trim() ? { password: form.password.trim() } : {}),
      };
      if (mode === "create") {
        await usersApi.create(payload);
        notify("success", "User created");
      } else if (selected) {
        await usersApi.update(selected.id, payload);
        notify("success", "User updated");
      }
      await loadUsers(1);
      window.dispatchEvent(new Event(AGENTS_REFRESH_EVENT));
      setModalOpen(false);
      if (usersModalOpen) closeUsersModal();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to save user";
      notify("error", msg);
    } finally {
      setSaving(false);
    }
  };

  const selectedProductLabel = selected?.productId ? productNameById[selected.productId] || `#${selected.productId}` : "—";

  // Agents get one permanent first login; later logins need admin permission.
  // `loginWindow` is computed by the API so the rule lives in exactly one place.
  const loginWindow = selected?.role === "AGENT" ? selected.loginWindow : null;

  const setLoginPermission = async (allow) => {
    if (!selected || !canClearDevice) return;
    if (allow) {
      const ok = window.confirm(
        `Let ${selected.fullName} sign in once more?\n\n` +
        "Their next successful login uses this permission up. After that they will need permission again.",
      );
      if (!ok) return;
    }
    try {
      await usersApi.setLoginPermission(selected.id, allow ? 1 : 0);
      notify("success", allow ? "Login permission granted" : "Login permission revoked");
      await loadUsers(meta.page || 1);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to update login permission";
      notify("error", msg);
    }
  };

  const handleClearDevice = async () => {
    if (!selected || !canClearDevice) return;
    const ok = window.confirm("Remove device binding for this agent?\nThey will need to log in again from their mobile.");
    if (!ok) return;
    try {
      await usersApi.clearDevice(selected.id);
      notify("success", "Device removed for agent");
      // Refresh list & details
      await loadUsers(meta.page || 1);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to remove device";
      notify("error", msg);
    }
  };

  return (
    <div className="users-grid" style={styles.wrap}>
      <div className="users-list-panel" style={styles.listPanel}>
        <div style={styles.listHeader}>
          <SectionLabel>USERS</SectionLabel>
          <Btn size="sm" variant="primary" onClick={openCreate}>＋ Add</Btn>
        </div>

        <div className="filter-grid" style={styles.filters}>
          <Select value={filters.role} onChange={e => setFilters(f => ({ ...f, role: e.target.value }))}>
            <option value="">All Roles</option>
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </Select>
          <Select value={filters.productId} onChange={e => setFilters(f => ({ ...f, productId: e.target.value }))}>
            <option value="">All Products</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} placeholder="Search name, email, mobile" />
          <Btn size="sm" variant="ghost" onClick={() => loadUsers(1)}>Apply</Btn>
        </div>

        {loading && <div style={styles.info}>Loading users…</div>}
        {error && <div style={{ ...styles.info, color: T.sos }}>{error}</div>}
        {!loading && !error && users.length === 0 && (
          <div style={styles.info}>No users found.</div>
        )}

        <div style={styles.list}>
          {users.map(u => (
            <Card
              key={u.id}
              onClick={() => setSelectedId(u.id)}
              style={{ ...styles.listItem, ...(String(selectedId) === String(u.id) ? styles.listItemActive : {}) }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontWeight: 700, color: T.text, fontSize: 13 }}>{u.fullName || "Unnamed user"}</div>
                <div style={{ fontSize: 10, color: T.muted, fontFamily: T.fontMono }}>
                  {u.role}{u.status === "off" ? " · OFF" : ""}
                </div>
              </div>
              <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>{u.mobile}</div>
              <div style={{ fontSize: 10, color: T.muted2, marginTop: 4 }}>
                {u.role === "AGENT" ? `Product: ${productNameById[u.productId] || "—"}` : "Products: Multiple/Optional"}
              </div>
            </Card>
          ))}
        </div>

        <div style={styles.pagination}>
          <Btn size="sm" variant="ghost" disabled={meta.page <= 1} onClick={() => loadUsers(meta.page - 1)}>Prev</Btn>
          <span style={styles.pageLabel}>Page {meta.page} of {meta.totalPages}</span>
          <Btn size="sm" variant="ghost" disabled={meta.page >= meta.totalPages} onClick={() => loadUsers(meta.page + 1)}>Next</Btn>
        </div>
      </div>

      <div style={styles.detailPanel}>
        <SectionLabel
          action={selected && (
            <div style={{ display: "flex", gap: 8 }}>
              {canClearDevice && (
                <Btn size="sm" variant="ghost" onClick={handleClearDevice}>
                  Remove Device
                </Btn>
              )}
              <Btn size="sm" variant="outline" onClick={() => openEdit(selected || users[0])}>
                Edit
              </Btn>
            </div>
          )}
        >
          USER DETAILS
        </SectionLabel>
        {selected ? (
          <div>
            <div style={styles.detailName}>{selected.fullName}</div>
            <div style={styles.detailSub}>{selected.email}</div>
            <div style={styles.detailSub}>{selected.mobile}</div>
            <div style={{ marginTop: 10, fontSize: 11, color: T.muted }}>
              Role: <span style={{ color: T.text, fontFamily: T.fontMono }}>{selected.role}</span>
            </div>
            <div style={{ marginTop: 6, fontSize: 11, color: T.muted }}>
              Product: <span style={{ color: T.text, fontFamily: T.fontMono }}>{selectedProductLabel}</span>
            </div>
            <div style={styles.metaRow}>
              <div style={styles.metaItem}>
                <div style={styles.metaLabel}>CITY</div>
                <div style={styles.metaVal}>{selected.city || "—"}</div>
              </div>
            <div style={styles.metaItem}>
              <div style={styles.metaLabel}>DISTRICT</div>
              <div style={styles.metaVal}>{selected.district || "—"}</div>
            </div>
            <div style={styles.metaItem}>
              <div style={styles.metaLabel}>AREA</div>
              <div style={styles.metaVal}>{selected.area || "—"}</div>
            </div>
            <div style={styles.metaItem}>
              <div style={styles.metaLabel}>DESIGNATION</div>
              <div style={styles.metaVal}>{selected.designation || "—"}</div>
            </div>
              <div style={styles.metaItem}>
                <div style={styles.metaLabel}>PINCODE</div>
                <div style={styles.metaVal}>{selected.pincode || "—"}</div>
              </div>
              <div style={styles.metaItem}>
                <div style={styles.metaLabel}>STATUS</div>
                <div style={styles.metaVal}>{selected.status || "—"}</div>
              </div>
            </div>
            {loginWindow && (
              <div style={styles.loginWindowBox}>
                <div style={styles.metaLabel}>APP LOGIN</div>
                <div style={{ marginTop: 6, fontSize: 11, color: T.muted }}>
                  Last login:{" "}
                  <span style={{ color: T.text, fontFamily: T.fontMono }}>
                    {fmtDate(loginWindow.lastLoginAt) || "never"}
                  </span>
                </div>
                {loginWindow.allowRelogin ? (
                  <div style={{ ...styles.loginWindowNote, color: T.acc3 }}>
                    Permission granted — their next login will use it up.
                  </div>
                ) : loginWindow.locked ? (
                  <div style={{ ...styles.loginWindowNote, color: T.sos }}>
                    Signed in. They cannot sign in again without permission.
                  </div>
                ) : (
                  <div style={{ ...styles.loginWindowNote, color: T.muted }}>
                    Free to sign in — first login does not need permission.
                  </div>
                )}
                {canClearDevice && (
                  <div style={{ marginTop: 10 }}>
                    {loginWindow.allowRelogin ? (
                      <Btn size="sm" variant="ghost" onClick={() => setLoginPermission(false)}>
                        Revoke Permission
                      </Btn>
                    ) : (
                      <Btn
                        size="sm"
                        variant={loginWindow.locked ? "primary" : "ghost"}
                        disabled={!loginWindow.locked}
                        onClick={() => setLoginPermission(true)}
                      >
                        Allow Login
                      </Btn>
                    )}
                  </div>
                )}
              </div>
            )}
            {selected.address && (
              <div style={{ marginTop: 12, fontSize: 11, color: T.muted }}>{selected.address}</div>
            )}
          </div>
        ) : (
          <div style={styles.info}>Select a user to view details.</div>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => { setModalOpen(false); if (usersModalOpen) closeUsersModal(); }} title={mode === "create" ? "Add User" : "Update User"} width={560}>
        {formError && <div style={{ color: T.sos, fontSize: 11, marginBottom: 10 }}>{formError}</div>}
        <div className="form-grid" style={styles.formGrid}>
          <Field label="FULL NAME">
            <Input value={form.fullName} onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))} />
          </Field>
          <Field label="EMAIL">
            <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          </Field>
          <Field label="MOBILE">
            <Input value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} />
          </Field>
          {(mode === "create" || (mode === "edit" && canEditPassword)) && (
            <Field label="PASSWORD">
              <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            </Field>
          )}
          <Field label="ROLE">
            <Select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value, productIds: [] }))}>
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </Select>
          </Field>
          <Field label="STATUS">
            <Select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              <option value="on">ON</option>
              <option value="off">OFF</option>
            </Select>
          </Field>
          <Field label="ALTERNATE MOBILE">
            <Input value={form.alternateMobile} onChange={e => setForm(f => ({ ...f, alternateMobile: e.target.value }))} />
          </Field>
          <Field label="CITY">
            <Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
          </Field>
          <Field label="DISTRICT">
            <Input value={form.district} onChange={e => setForm(f => ({ ...f, district: e.target.value }))} />
          </Field>
          <Field label="AREA">
            <Input value={form.area} onChange={e => setForm(f => ({ ...f, area: e.target.value }))} />
          </Field>
          <Field label="DESIGNATION">
            <Input value={form.designation} onChange={e => setForm(f => ({ ...f, designation: e.target.value }))} />
          </Field>
          <Field label="PINCODE">
            <Input value={form.pincode} onChange={e => setForm(f => ({ ...f, pincode: e.target.value }))} />
          </Field>
          <Field label="LATITUDE">
            <Input value={form.latitude} onChange={e => setForm(f => ({ ...f, latitude: e.target.value }))} />
          </Field>
          <Field label="LONGITUDE">
            <Input value={form.longitude} onChange={e => setForm(f => ({ ...f, longitude: e.target.value }))} />
          </Field>
        </div>
        <Field label="ADDRESS">
          <Textarea value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} rows={3} />
        </Field>

        <div style={{ marginTop: 8 }}>
          <SectionLabel>PRODUCTS</SectionLabel>
          {products.length === 0 && (
            <div style={{ fontSize: 11, color: T.muted }}>No products available.</div>
          )}
          <div style={styles.productGrid}>
            {products.map(p => {
              const checked = form.productIds.includes(p.id);
              return (
                <label key={p.id} style={styles.productOption}>
                  <input
                    type={form.role === "AGENT" ? "radio" : "checkbox"}
                    name="productIds"
                    checked={checked}
                    onChange={() => updateProductSelection(p.id)}
                  />
                  <span style={{ fontSize: 12, color: T.text }}>{p.name}</span>
                </label>
              );
            })}
          </div>
          <div style={{ fontSize: 10, color: T.muted2, marginTop: 6 }}>
            {form.role === "AGENT" ? "Agent must select exactly one product." : "Other roles can select multiple or none."}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12 }}>
          <Btn variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={saveUser} disabled={saving}>
            {saving ? "Saving…" : mode === "create" ? "Add User" : "Update User"}
          </Btn>
        </div>
      </Modal>
    </div>
  );
}

const styles = {
  wrap: { flex: 1, display: "grid", gridTemplateColumns: "360px 1fr", gap: 16, padding: 16, overflow: "hidden" },
  listPanel: { background: T.s1, border: `1px solid ${T.border}`, borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", overflow: "hidden" },
  listHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 },
  filters: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 },
  list: { overflowY: "auto", paddingRight: 4, display: "flex", flexDirection: "column", gap: 8 },
  listItem: { cursor: "pointer" },
  listItemActive: { borderColor: T.acc, boxShadow: `0 0 0 1px ${T.acc}66 inset` },
  detailPanel: { background: T.s1, border: `1px solid ${T.border}`, borderRadius: 12, padding: 18, overflow: "auto" },
  detailName: { fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4 },
  detailSub: { fontSize: 12, color: T.muted },
  metaRow: { display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 },
  metaItem: { background: T.s2, border: `1px solid ${T.border2}`, borderRadius: 8, padding: "8px 10px", minWidth: 140 },
  metaLabel: { fontSize: 9, color: T.muted2, fontFamily: T.fontMono, letterSpacing: "1px" },
  metaVal: { fontSize: 12, color: T.text, fontFamily: T.fontMono, marginTop: 4 },
  loginWindowBox: { marginTop: 14, background: T.s2, border: `1px solid ${T.border2}`, borderRadius: 8, padding: "10px 12px" },
  loginWindowNote: { fontSize: 11, marginTop: 6, lineHeight: 1.5 },
  info: { fontSize: 12, color: T.muted, padding: "8px 4px" },
  pagination: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 },
  pageLabel: { fontSize: 11, color: T.muted, fontFamily: T.fontMono },
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  productGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 },
  productOption: { display: "flex", alignItems: "center", gap: 6, background: T.s2, border: `1px solid ${T.border2}`, borderRadius: 8, padding: "6px 8px" },
};
